import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFetchJobs, useFetchOrganizations } from '../../hooks/api';
import type { TransformationDefinition, Migration } from '../../types';
//import { Tooltip } from '../../components/Tooltip';
import { Modal } from '../../components/Modals/Shared/Modal';
import { PreMigrationConfirmModal } from '../../components/Modals/Migration/PreMigrationConfirmModal';
import Spinner from '../../components/migratorDashboard/Spinner';
import { logger } from '../../utils/logger';
import { useModalState } from '../../hooks/ui';
import { approveTransformationsAndStartImport } from '../../services/Migration/updateMigrationService';
import {
  downloadMigrationResults,
  NoResultsAvailableError
} from '../../services/Migration/downloadMigrationResultsService';
import { MigrationProgressStepper } from '../../components/ProgressTracker/MigrationProgressStepper';
import { APPROVE_TRANSFORMATIONS_STEPS } from '../../utils/migrationSteps';
import { useToast } from '../../components/Toast/ToastProvider';
import { useAuth } from '../../hooks/auth';
import { toFriendlyName } from '../../utils/formatters';
import { formatRegion } from '../../utils/formatters';
import { getTypeLabelByApiName } from '../../utils/resourceTypes';
import { ui } from '../../config/uiSizes';
import { exampleMatchesTransformation } from '../../types/transformations';

/**
 * Checks if a file is a common Terraform infrastructure file that should be
 * excluded from the unaffected files list (e.g., main.tf, variables.tf, provider.tf).
 */
const isCommonTerraformFile = (filePath: string): boolean => {
  const fileName = filePath.split('/').pop() ?? filePath;
  const normalizedName = fileName.toLowerCase();
  return (
    normalizedName === 'main.tf' ||
    normalizedName === 'main.tf.json' ||
    normalizedName === 'variables.tf' ||
    normalizedName === 'variables.tf.json' ||
    normalizedName === 'provider.tf' ||
    normalizedName === 'provider.tf.json' ||
    normalizedName === 'providers.tf' ||
    normalizedName === 'providers.tf.json'
  );
};

/**
 * Converts a file path like "genesyscloud/genesyscloud_architect_datatable.tf.json"
 * into a friendly display label like "Data Table" (or "Architect Datatable" if no label exists).
 * Uses the transformation chain: API type → friendly name → label.
 */
const formatFilePath = (filePath: string): string => {
  const fileName = filePath.split('/').pop() ?? filePath;
  // Extract the resource type from the filename
  const fullResourceType = fileName.replace(/\.tf\.json$/, '');
  // Convert to friendly name (e.g., 'genesyscloud_auth_role' → 'Auth Role')
  const friendlyName = toFriendlyName(fullResourceType);
  // Apply label mapping (e.g., 'Auth Role' → 'Roles', 'User' → 'People')
  return getTypeLabelByApiName(friendlyName);
};

const ApproveTransformationsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { showToast } = useToast();
  const { loading, error, fetchJobs } = useFetchJobs();
  const selectedJob = location.state?.job as Migration | undefined;
  const { organizations, fetchOrganizations } = useFetchOrganizations();

  // Log page mount for traceability
  React.useEffect(() => {
    logger.info('Page: ApproveTransformations mounted', { jobId: selectedJob?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch organizations on mount
  React.useEffect(() => {
    if (accessToken) {
      fetchOrganizations();
    }
  }, [accessToken, fetchOrganizations]);
  const [selectedTransformation, setSelectedTransformation] =
    useState<TransformationDefinition | null>(null);
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());
  const [startImport, setStartImport] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [showPreMigrationConfirm, setShowPreMigrationConfirm] = useState(false);
  const { modalState, handleModalClose, handleTransformationViewDetailsClick } = useModalState();

  useEffect(() => {
    // If no job was passed, redirect back to dashboard
    if (!selectedJob) {
      navigate('/dashboard');
      return;
    }
    fetchJobs();
  }, [fetchJobs, selectedJob, navigate]);

  // Extract transformations from the selected job only
  const allTransformations = useMemo(() => {
    const transformations: TransformationDefinition[] = [];

    if (!selectedJob) return transformations;

    if (selectedJob.status === 'TransformationComplete' && selectedJob.transformationConfig) {
      const configs = Array.isArray(selectedJob.transformationConfig)
        ? selectedJob.transformationConfig
        : [selectedJob.transformationConfig];

      configs.forEach((config) => {
        config.transformations.forEach((transform: TransformationDefinition) => {
          if (transform.enabled) {
            transformations.push(transform);
          }
        });
      });
    }

    return transformations;
  }, [selectedJob]);

  const camelToWords = (s: string) => s.replace(/([A-Z])/g, ' $1').trim();

  // Group transformations by combined resource types (deduplicated)
  const groupedTransformations = useMemo(() => {
    const groups = new Map<string, TransformationDefinition[]>();
    const processedTransformations = new Set<string>();

    allTransformations.forEach((transform) => {
      const types = transform.applyToTypes || ['General'];
      const transformKey = `${transform.name}-${transform.order || 0}`;

      // Skip if already processed
      if (processedTransformations.has(transformKey)) {
        return;
      }

      // Each transformation gets its own card — key by name+order
      const combinedKey = transformKey;

      if (!groups.has(combinedKey)) {
        groups.set(combinedKey, []);
      }
      groups.get(combinedKey)?.push({ ...transform, applyToTypes: types });
      processedTransformations.add(transformKey);
    });

    return Array.from(groups.entries())
      .map(([, transformations]) => ({
        resourceType: (transformations[0]?.applyToTypes ?? ['General']).sort().join(', '),
        transformations
      }))
      .sort((a, b) => a.resourceType.localeCompare(b.resourceType));
  }, [allTransformations]);

  const handleClose = () => {
    handleModalClose();
    setSelectedTransformation(null);
  };

  const enabledCount = allTransformations.length;

  const handleApproveAll = async () => {
    if (!selectedJob?.id) {
      logger.error('No migration ID available');
      return;
    }

    setStartImport(true);
    try {
      // Call the API to advance the migration to the next status (ImportQueued)
      const response = await approveTransformationsAndStartImport(
        accessToken || '',
        selectedJob.id
      );

      // Mark all transformations as approved in local state
      const ids = allTransformations.map((t) => `${t.name}-${t.order || 0}`);
      setApprovedIds(new Set(ids));

      logger.info('Transformations approved and import started', {
        migrationId: selectedJob.id,
        response
      });

      // Navigate back to dashboard with success message
      navigate('/dashboard', {
        state: {
          message: `Import queued for ${selectedJob.name}`,
          updatedMigration: response
        }
      });
    } catch (err) {
      logger.error('Error approving transformations', { error: err });
      console.error('Error approving transformations', err);
      // You might want to show an error message to the user here
    } finally {
      setStartImport(false);
    }
  };

  if (!selectedJob) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">
          No migration selected. Redirecting...
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
          <Spinner />
          <span>Loading transformations...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600 dark:text-red-400">Error: {error}</div>
      </div>
    );
  }

  return (
    <>
      <div className={`${ui.page.cardPadding}`}>
        <div className="flex flex-col flex-1 rounded-2xl border-1 border-gray-300 bg-gray-200 shadow-xl dark:border-gray-700 dark:bg-gray-800 overflow-hidden mx-1 mt-0 mb-2 p-1">
          <div
            className={`flex flex-col flex-1 rounded-2xl border border-gray-200 bg-gray-100 shadow-xl dark:border-gray-600 dark:bg-gray-700 overflow-hidden m-2 ${ui.page.cardPadding}`}
          >
            {/* Main content card */}
            <div
              className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 ${ui.page.cardPadding}`}
            >
              <div className={`flex items-start ${ui.mb.lg}`}>
                <div className="inline-flex flex-col items-start">
                  <h2
                    className={` ${ui.text.pageTitle} font-extrabold leading-none tracking-tight text-gray-800 dark:text-gray-100`}
                  >
                    {selectedJob.name}
                  </h2>
                  <div
                    aria-hidden="true"
                    className="mt-2 h-1 w-16 bg-blue-600 dark:bg-blue-500 rounded"
                  />
                </div>
              </div>

              {/* Migration Progress Stepper */}
              {!selectedJob.isSnapshot && (
                <MigrationProgressStepper steps={APPROVE_TRANSFORMATIONS_STEPS} />
              )}

              {/* Header */}
              <div className={`${ui.mb.section}`}>
                <div className={`flex items-baseline ${ui.gap.sm} ${ui.mb.title}`}>
                  <h1
                    className={` ${ui.text.pageTitle} font-bold text-gray-800 dark:text-gray-100`}
                  >
                    Review transformations
                  </h1>
                  <span
                    className={`inline-flex items-center ${ui.button.sm} rounded-full ${ui.text.label} font-semibold bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-sm`}
                  >
                    {enabledCount} enabled
                  </span>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3 border-l-4 border-blue-600">
                  <p className={`${ui.text.label} font-semibold text-gray-700 dark:text-gray-300`}>
                    The export for{' '}
                    <span className="font-semibold text-green-600 dark:text-green-500">
                      {selectedJob.name}
                    </span>{' '}
                    has completed successfully. Review and approve transformations before applying
                    them to the target organization{' '}
                    <span className="font-semibold text-green-600 dark:text-green-500">
                      {selectedJob.targetOrgName ? (
                        <>
                          {selectedJob.targetOrgName} - {(() => {
                            const org = organizations.find(o => o.name === selectedJob.targetOrgName);
                            return formatRegion(org?.region);
                          })()}
                        </>
                      ) : ''}
                    </span>
                    .
                  </p>
                </div>
              </div>

              <>
                {/* Scrollable content area */}
                <div
                  className={`bg-white/900 dark:bg-gray-900/90 backdrop-blur-sm rounded-2xl border border-gray-300 dark:border-gray-600 shadow-xl ${ui.page.cardPadding} min-h-[700px]`}
                >
                  <div className="space-y-8">
                    {/* Overall Changes Heading */}
                    <div className={ui.mb.title}>
                      <span
                        className={`${ui.text.label} font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500`}
                      >
                        Summary
                      </span>
                      <div className={`flex items-center ${ui.gap.md} mt-0.5`}>
                        <h2
                          className={`${ui.text.sectionTitle} font-bold text-gray-800 dark:text-gray-100 leading-tight whitespace-nowrap`}
                        >
                          Overall Transformation Statistics
                        </h2>
                        <span className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                      </div>
                    </div>

                    {/* Changes summary with file list */}
                    {selectedJob.job2Transform?.changesSummary &&
                      (() => {
                        const summary = selectedJob.job2Transform!.changesSummary!;

                        // Filter out common Terraform files from unchanged files
                        const unchangedFilesFiltered = (summary.unchangedFiles ?? []).filter(
                          (f) => !isCommonTerraformFile(f)
                        );

                        // Recalculate total and percentage without common Terraform files
                        const total =
                          (summary.filesModified ?? 0) + unchangedFilesFiltered.length;
                        const pct = total > 0 ? ((summary.filesModified ?? 0) / total) * 100 : 0;
                        const circumference = 2 * Math.PI * 14;
                        const modified = summary.modifiedFiles ?? [];
                        const unchanged = unchangedFilesFiltered;
                        return (
                          <div
                            className={`${ui.mb.section} rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900`}
                          >
                            {/* Header row with ring */}
                            <div
                              className={`flex items-center ${ui.gap.lg} px-5 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 rounded-t-xl`}
                            >
                              {/* Ring with hover tooltip */}
                              <div className="relative flex-shrink-0 group/ring cursor-default">
                                <div className="relative w-14 h-14">
                                  <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                                    <circle
                                      cx="18"
                                      cy="18"
                                      r="14"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                      className="text-gray-200 dark:text-gray-700"
                                    />
                                    <circle
                                      cx="18"
                                      cy="18"
                                      r="14"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                      strokeDasharray={`${(pct / 100) * circumference} ${circumference}`}
                                      className="text-blue-500 dark:text-blue-400"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-extrabold text-gray-700 dark:text-gray-200">
                                    {Math.round(pct)}%
                                  </span>
                                </div>
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/ring:block z-50 pointer-events-none">
                                  <div
                                    className={`bg-gray-900 dark:bg-gray-700 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg whitespace-nowrap`}
                                  >
                                    {Math.round(pct)}% of Resource Types were modified
                                  </div>
                                  <div className="w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 mx-auto -mt-1" />
                                </div>
                              </div>
                              {/* Stat badges */}
                              <div className={`flex flex-wrap ${ui.gap.sm} flex-1`}>
                                {[
                                  {
                                    value: summary.filesModified ?? 0,
                                    label: 'Resource Types Modified',
                                    sub: 'These Resource Types had transformation changes applied during processing',
                                    cls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700'
                                  },
                                  {
                                    value: unchangedFilesFiltered.length,
                                    label: 'Resource Types Unchanged',
                                    sub: 'These Resource Types were scanned but required no modifications',
                                    cls: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                                  },
                                  {
                                    value: summary.referencesUpdated ?? 0,
                                    label: 'References Updated',
                                    sub: 'Cross-resource name references rewritten to match the target org',
                                    cls: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700'
                                  },
                                  {
                                    value: summary.divisionAssignmentsApplied ?? 0,
                                    label: 'Division Assignments',
                                    sub: 'Resource Types automatically assigned to their target org divisions',
                                    cls: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700'
                                  }
                                ].map(({ label, sub, value, cls }) => (
                                  <div
                                    key={label}
                                    className={`relative group/badge flex items-center ${ui.gap.sm} px-3 py-2 rounded-lg border cursor-default ${cls}`}
                                  >
                                    <span className={`${ui.text.label} font-extrabold`}>
                                      {value}
                                    </span>
                                    <span className={`${ui.text.xs} font-semibold opacity-80`}>
                                      {label}
                                    </span>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/badge:block z-50 pointer-events-none w-48">
                                      <div
                                        className={`bg-gray-900 dark:bg-gray-700 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg text-center leading-snug`}
                                      >
                                        {sub}
                                      </div>
                                      <div className="w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 mx-auto -mt-1" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {/* Side by side */}
                            <div className="grid grid-cols-2 divide-x divide-gray-100 dark:divide-gray-800 rounded-b-xl overflow-hidden">
                              {[
                                {
                                  label: 'Modified',
                                  files: modified,
                                  dot: 'bg-blue-400',
                                  badge:
                                    'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300',
                                  title: 'text-blue-700 dark:text-blue-400'
                                },
                                {
                                  label: 'Unchanged',
                                  files: unchanged,
                                  dot: 'bg-gray-300 dark:bg-gray-600',
                                  badge: 'bg-gray-100 dark:bg-gray-800 text-gray-500',
                                  title: 'text-gray-500 dark:text-gray-400'
                                }
                              ].map(({ label, files, dot, badge, title }) => (
                                <div key={label}>
                                  <div
                                    className={`flex items-center justify-between ${ui.input.base} border-b border-gray-100 dark:border-gray-800`}
                                  >
                                    <span
                                      className={` ${ui.text.sm} font-bold uppercase tracking-wider ${title}`}
                                    >
                                      {label}
                                    </span>
                                    <span
                                      className={`${ui.text.xs} font-extrabold px-2 py-0.5 rounded-full ${badge}`}
                                    >
                                      {files.length}
                                    </span>
                                  </div>
                                  <div className="max-h-44 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800">
                                    {files.map((f) => (
                                      <div
                                        key={f}
                                        className={`flex items-center ${ui.gap.sm} ${ui.input.base} hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors`}
                                        title={f}
                                      >
                                        <span
                                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`}
                                        />
                                        <span
                                          className={`${ui.text.xs} font-mono text-gray-600 dark:text-gray-300 truncate`}
                                        >
                                          {formatFilePath(f)}
                                        </span>
                                      </div>
                                    ))}
                                    {files.length === 0 && (
                                      <div
                                        className={`w-full py-6 text-center ${ui.text.xs} text-gray-400 dark:text-gray-500 italic`}
                                      >
                                        None
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                    <div className={`${ui.mb.title}`}>
                      <span
                        className={`${ui.text.sm} font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500`}
                      >
                        Transformations
                      </span>
                      <div className={`flex items-center ${ui.gap.md} mt-0.5`}>
                        <h2
                          className={`${ui.text.sectionTitle} font-bold text-gray-800 dark:text-gray-100 leading-tight whitespace-nowrap`}
                        >
                          Applied Transformations
                        </h2>
                        <span className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                      </div>
                    </div>

                    {groupedTransformations.map((group) => {
                      const firstTransform = group.transformations[0];
                      const collapseKey = `${firstTransform?.name ?? ''}-${firstTransform?.order ?? 0}`;
                      const isCollapsed = collapsedGroups.has(collapseKey);
                      const toggleGroup = () =>
                        setCollapsedGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(collapseKey)) next.delete(collapseKey);
                          else next.add(collapseKey);
                          return next;
                        });
                      const groupTitle = camelToWords(firstTransform?.name ?? '');
                      return (
                        <div key={collapseKey}>
                          <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
                            <div
                              className={`flex items-center ${ui.gap.lg} px-5 py-6 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors duration-150`}
                            >
                              <button
                                type="button"
                                onClick={toggleGroup}
                                className={`flex items-center ${ui.gap.md} flex-1 min-w-0 text-left`}
                              >
                                <svg
                                  className={`${ui.svg.sm} text-gray-400 dark:text-gray-500 transition-transform duration-200 shrink-0 ${isCollapsed ? '' : 'rotate-90'}`}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <div className="flex flex-col min-w-0">
                                  <span
                                    className={`${ui.text.label} font-bold text-gray-800 dark:text-gray-100 leading-tight`}
                                  >
                                    {groupTitle}
                                  </span>
                                  {firstTransform?.description && (
                                    <span
                                      className={`${ui.text.sm} text-gray-600 dark:text-gray-300 truncate leading-tight mt-0.5`}
                                    >
                                      {firstTransform.description}
                                    </span>
                                  )}
                                </div>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (firstTransform) {
                                    setSelectedTransformation(firstTransform);
                                    handleTransformationViewDetailsClick(firstTransform);
                                  }
                                }}
                                className={`shrink-0 px-3 py-1.5 ${ui.text.xs} font-medium text-blue-500 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:shadow-sm active:scale-95 transition-all duration-150`}
                              >
                                View details
                              </button>
                            </div>
                            {/* Collapsible body */}
                            {!isCollapsed && (
                              <div className={ui.page.cardPadding}>
                                {(() => {
                                  const groupStats = (
                                    selectedJob.job2Transform?.changesSummary
                                      ?.transformationStats ?? []
                                  ).filter((s) =>
                                    exampleMatchesTransformation(
                                      s.transformationName,
                                      firstTransform?.name ?? ''
                                    )
                                  );
                                  const totalChanges = groupStats.reduce(
                                    (acc, s) => acc + (s.changesCount ?? 0),
                                    0
                                  );
                                  const allAffectedFiles = groupStats.flatMap(
                                    (s) => s.affectedFiles ?? []
                                  );
                                  const uniqueAffectedFiles = [...new Set(allAffectedFiles)];
                                  return (
                                    <div className={`${ui.mb.section} space-y-3`}>
                                      {/* changes count banner */}
                                      <div className={`flex items-baseline ${ui.gap.sm} py-1`}>
                                        <h2
                                          className={`${ui.text.lgTitle} font-black tabular-nums leading-none shrink-0 ${totalChanges === 0 ? 'text-gray-400 dark:text-gray-600' : 'text-gray-900 dark:text-gray-100'}`}
                                        >
                                          {totalChanges.toLocaleString()}
                                        </h2>
                                        <span
                                          className={`${ui.text.label} font-semibold text-gray-700 dark:text-gray-300 shrink-0`}
                                        >
                                          Total Changes
                                        </span>
                                        <span
                                          className={`${ui.text.xs} text-gray-400 dark:text-gray-500`}
                                        >
                                          {totalChanges === 0
                                            ? 'no changes were made by this transformation'
                                            : 'applied by this transformation'}
                                        </span>
                                      </div>
                                      <div className="border-b border-gray-200 dark:border-gray-700" />

                                      {/* Selected Resource Types */}
                                      {(() => {
                                        const selectedTypes = group.resourceType.split(', ');
                                        const allGroupStats = (
                                          selectedJob.job2Transform?.changesSummary
                                            ?.transformationStats ?? []
                                        ).filter((s) =>
                                          exampleMatchesTransformation(
                                            s.transformationName,
                                            firstTransform?.name ?? ''
                                          )
                                        );
                                        const affectedDisplayNames = new Set(
                                          allGroupStats.flatMap((s) =>
                                            (s.affectedFiles ?? []).map(formatFilePath)
                                          )
                                        );
                                        // Normalize each selected type to a label name for comparison.
                                        // applyToTypes can be a mix of display names ("Auth Division") and
                                        // terraform types ("genesyscloud_group"), so we normalise via
                                        // toFriendlyName → getTypeLabelByApiName transformation chain.
                                        const normalizeType = (r: string): string => {
                                          // First convert to friendly name format
                                          const friendlyName = toFriendlyName(r);
                                          // Then apply label mapping (e.g., 'User' → 'People')
                                          return getTypeLabelByApiName(friendlyName);
                                        };
                                        const hasStats = allGroupStats.length > 0;
                                        const totalChanges = allGroupStats.reduce(
                                          (acc, s) => acc + (s.changesCount ?? 0),
                                          0
                                        );
                                        const unaffectedTypesCount = !hasStats
                                          ? 0
                                          : totalChanges === 0
                                            ? selectedTypes.length
                                            : selectedTypes.filter(
                                              (r) => !affectedDisplayNames.has(normalizeType(r))
                                            ).length;
                                        return (
                                          <div
                                            className={`${ui.mb.section} rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden`}
                                          >
                                            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                              <div className={`flex items-center ${ui.gap.sm}`}>
                                                <div
                                                  className={`${ui.svg.sm} rounded-md bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0`}
                                                >
                                                  <svg
                                                    className="w-3 h-3 text-blue-500 dark:text-blue-400"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                  >
                                                    <path
                                                      fillRule="evenodd"
                                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                      clipRule="evenodd"
                                                    />
                                                  </svg>
                                                </div>
                                                <div className="flex flex-col leading-none gap-0.5">
                                                  <span
                                                    className={`${ui.text.xs} font-bold text-gray-600 dark:text-gray-400`}
                                                  >
                                                    Selected Resource Types
                                                  </span>
                                                  <span
                                                    className={`${ui.text.sm} text-gray-500 dark:text-gray-500`}
                                                  >
                                                    This transformation was configured to run
                                                    against the following resource types
                                                  </span>
                                                </div>
                                              </div>
                                              <div
                                                className={`flex items-center ${ui.gap.sm} shrink-0`}
                                              >
                                                <span
                                                  className={`${ui.text.xxs} font-semibold px-2 py-0.5 rounded-full border bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700`}
                                                >
                                                  {unaffectedTypesCount} Resource type
                                                  {unaffectedTypesCount !== 1 ? 's' : ''}{' '}
                                                  unaffected
                                                </span>
                                                <span
                                                  className={`${ui.text.xs} font-extrabold px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300`}
                                                >
                                                  {selectedTypes.length} type
                                                  {selectedTypes.length !== 1 ? 's' : ''}
                                                </span>
                                              </div>
                                            </div>
                                            <div
                                              className={`flex flex-wrap ${ui.gap.xs} px-4 py-3 bg-white dark:bg-gray-900`}
                                            >
                                              {selectedTypes.map((r) => {
                                                const displayName = normalizeType(r);
                                                const isAffected =
                                                  !hasStats ||
                                                  (totalChanges > 0 &&
                                                    affectedDisplayNames.has(displayName));
                                                return isAffected ? (
                                                  <span
                                                    key={r}
                                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg ${ui.text.xs} font-semibold bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300`}
                                                  >
                                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 shrink-0" />
                                                    {displayName}
                                                  </span>
                                                ) : (
                                                  <span
                                                    key={r}
                                                    title="Not affected by this transformation"
                                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg ${ui.text.xs} font-semibold bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400`}
                                                  >
                                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 shrink-0" />
                                                    {displayName}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })()}

                                      {/* Affected Resource Types box */}
                                      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                          <div className={`flex items-center ${ui.gap.sm}`}>
                                            <div
                                              className={`${ui.svg.sm} rounded-md bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0`}
                                            >
                                              <svg
                                                className="w-3 h-3 text-blue-500 dark:text-blue-400"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                              >
                                                <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                                              </svg>
                                            </div>
                                            <div className="flex flex-col leading-none gap-0.5">
                                              <span
                                                className={`${ui.text.xs} font-bold text-gray-600 dark:text-gray-400`}
                                              >
                                                Affected Resource Types
                                              </span>
                                              <span
                                                className={`${ui.text.sm} text-gray-500 dark:text-gray-500`}
                                              >
                                                Resource types modified by this transformation
                                              </span>
                                            </div>
                                          </div>
                                          <span
                                            className={`${ui.text.xs} font-extrabold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300`}
                                          >
                                            {uniqueAffectedFiles.length}
                                          </span>
                                        </div>
                                        <div
                                          className={`flex flex-wrap ${ui.gap.xs} px-4 py-3 bg-white dark:bg-gray-900`}
                                        >
                                          {uniqueAffectedFiles.length === 0 ? (
                                            <div
                                              className={`w-full py-4 text-center ${ui.text.xs} text-gray-400 dark:text-gray-500 italic`}
                                            >
                                              None
                                            </div>
                                          ) : (
                                            uniqueAffectedFiles.map((f, fi) => (
                                              <span
                                                key={fi}
                                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg ${ui.text.xs} font-semibold bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300`}
                                              >
                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 shrink-0" />
                                                {formatFilePath(f)}
                                              </span>
                                            ))
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}

                                <div className="space-y-4">
                                  {/* Examples */}
                                  {(() => {
                                    const groupExamples = (
                                      selectedJob.job2Transform?.changesSummary?.examples ?? []
                                    ).filter((ex) =>
                                      exampleMatchesTransformation(
                                        ex.transformationType,
                                        firstTransform?.name ?? ''
                                      )
                                    );
                                    return (
                                      <div
                                        className={`rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${ui.mb.section}`}
                                      >
                                        {' '}
                                        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                          <div className={`flex items-center ${ui.gap.sm}`}>
                                            <div
                                              className={`${ui.svg.sm} rounded-md bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0`}
                                            >
                                              <svg
                                                className="w-3 h-3 text-blue-500 dark:text-blue-400"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                              >
                                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                                <path
                                                  fillRule="evenodd"
                                                  d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                            </div>
                                            <div className="flex flex-col leading-none gap-0.5">
                                              <span
                                                className={`${ui.text.xs} font-bold text-gray-600 dark:text-gray-400`}
                                              >
                                                Transformation Examples
                                              </span>
                                              <span
                                                className={`${ui.text.sm} text-gray-500 dark:text-gray-500`}
                                              >
                                                Sample before and after values per transformation
                                                step
                                              </span>
                                            </div>
                                          </div>
                                          <span
                                            className={`${ui.text.xs} font-extrabold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300`}
                                          >
                                            {groupExamples.length}
                                          </span>
                                        </div>
                                        {groupExamples.length === 0 ? (
                                          <div
                                            className={`py-4 text-center ${ui.text.xs} text-gray-400 dark:text-gray-500 italic bg-white dark:bg-gray-900`}
                                          >
                                            None
                                          </div>
                                        ) : (
                                          <div
                                            className={`max-h-72 overflow-y-auto ${ui.page.smPadding} bg-white dark:bg-gray-900`}
                                          >
                                            <div className={`grid grid-cols-2 ${ui.gap.sm}`}>
                                              {groupExamples.map((ex, i) => (
                                                <div
                                                  key={i}
                                                  className="rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/30 dark:bg-blue-900/10 px-3 py-2.5 space-y-2 relative overflow-hidden"
                                                >
                                                  <div className="absolute top-0 right-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800/30 -translate-y-1/2 translate-x-1/2" />
                                                  <div
                                                    className={`flex items-center ${ui.gap.xs} flex-wrap`}
                                                  >
                                                    <span
                                                      className={`px-1.5 py-0.5 rounded-full ${ui.text.xxs} font-bold uppercase bg-blue-200 dark:bg-blue-800/50 text-blue-800 dark:text-blue-300`}
                                                    >
                                                      {ex.transformationType}
                                                    </span>
                                                    <span
                                                      className={`px-1.5 py-0.5 rounded-full ${ui.text.xxs} font-semibold bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400`}
                                                    >
                                                      {getTypeLabelByApiName(toFriendlyName(ex.resourceType))}
                                                    </span>{' '}
                                                  </div>
                                                  <div
                                                    className={`grid grid-cols-2 ${ui.gap.xs}`}
                                                  >
                                                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1.5">
                                                      <div
                                                        className={`${ui.text.xs} font-bold uppercase text-gray-400 dark:text-gray-500 mb-0.5`}
                                                      >
                                                        Before
                                                      </div>
                                                      <div
                                                        className={`${ui.text.xs} font-mono text-gray-600 dark:text-gray-400 break-all`}
                                                      >
                                                        {ex.originalValue}
                                                      </div>
                                                    </div>
                                                    <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 px-2 py-1.5">
                                                      <div
                                                        className={`${ui.text.xs} font-bold uppercase text-blue-500 dark:text-blue-400 mb-0.5`}
                                                      >
                                                        After
                                                      </div>
                                                      <div
                                                        className={`${ui.text.xs} font-mono text-blue-700 dark:text-blue-300 break-all`}
                                                      >
                                                        {ex.transformedValue}
                                                      </div>
                                                    </div>
                                                  </div>
                                                  {ex.filePath && (
                                                    <div
                                                      className={`flex items-center gap-1 ${ui.text.xxs} font-mono text-gray-400 dark:text-gray-500 truncate`}
                                                    >
                                                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 shrink-0" />
                                                      {formatFilePath(ex.filePath)}
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Action Bar - Bottom of page */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap items-center justify-end gap-3">
                    {/* Download Button */}
                    <div className="relative group/download">
                      <button
                        onClick={async () => {
                          if (!accessToken || !selectedJob) return;
                          setDownloadLoading(true);
                          try {
                            await downloadMigrationResults(accessToken, selectedJob.id);
                            logger.info('Download completed', { jobId: selectedJob.id });
                            showToast('Download started successfully', 'success');
                          } catch (error) {
                            if (error instanceof NoResultsAvailableError) {
                              showToast('No results are available for this migration', 'warning');
                            } else {
                              showToast('Failed to download migration results', 'error');
                            }
                            logger.error('Download failed', { error });
                          } finally {
                            setDownloadLoading(false);
                          }
                        }}
                        disabled={downloadLoading}
                        className={`${ui.button.lg} bg-purple-600 dark:bg-purple-700 text-white font-semibold rounded-lg hover:bg-purple-700 dark:hover:bg-purple-800 transition-all shadow-sm hover:shadow flex items-center justify-center ${ui.gap.sm} ${downloadLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {downloadLoading && (
                          <svg
                            className={`animate-spin ${ui.svg.sm} text-white`}
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            ></path>
                          </svg>
                        )}
                        <span>{downloadLoading ? 'Downloading...' : 'Download (Optional)'}</span>
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/download:block z-50 pointer-events-none w-56">
                        <div
                          className={`bg-gray-900 dark:bg-gray-700 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg leading-snug text-center`}
                        >
                          Optional: download transformed results for offline review. You can still
                          approve and start import without downloading.
                        </div>
                        <div className="w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 mx-auto -mt-1" />
                      </div>
                    </div>

                    {/* Edit Transformations Button */}
                    <div className="relative group/edit">
                      <button
                        onClick={() => {
                          navigate('/ApproveExportPage', { state: { job: selectedJob, isEdit: true } });
                        }}
                        className={`${ui.button.lg} bg-blue-600 dark:bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center ${ui.gap.sm}`}
                      >
                        <svg
                          className={`${ui.svg.sm}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        <span>Edit Transformations</span>
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/edit:block z-50 pointer-events-none w-56">
                        <div
                          className={`bg-gray-900 dark:bg-gray-700 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg leading-snug text-center`}
                        >
                          Return to export page to modify or remove transformations.
                        </div>
                        <div className="w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 mx-auto -mt-1" />
                      </div>
                    </div>

                    {/* Approve & Import Button */}
                    <div className="relative group/approve">
                      <button
                        type="button"
                        onClick={() => setShowPreMigrationConfirm(true)}
                        disabled={startImport || approvedIds.size === enabledCount}
                        className={`${ui.button.lg} bg-green-600 dark:bg-green-700 text-white font-semibold rounded-lg hover:bg-green-700 dark:hover:bg-green-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center ${ui.gap.md} group ${startImport ? 'bg-gray-300 dark:bg-gray-600 opacity-60 cursor-not-allowed' : ''}`}
                        aria-disabled={startImport || approvedIds.size === enabledCount}
                      >
                        <span className={`flex items-center ${ui.gap.sm}`}>
                          {startImport && (
                            <svg
                              className={`animate-spin ${ui.svg.sm} text-white`}
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              ></path>
                            </svg>
                          )}
                          <svg
                            className={`${ui.svg.sm} group-disabled:hidden`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>
                            {startImport
                              ? 'Starting Import...'
                              : approvedIds.size === enabledCount
                                ? 'Import Started'
                                : 'Approve & Import'}
                          </span>
                        </span>
                        <span
                          className={`ml-2 px-2 py-0.5 rounded ${ui.text.xs} font-normal bg-white/20 group-disabled:bg-transparent`}
                        >
                          {approvedIds.size}/{enabledCount}
                        </span>
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/approve:block z-50 pointer-events-none w-56">
                        <div
                          className={`bg-gray-900 dark:bg-gray-700 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg leading-snug text-center`}
                        >
                          Approve all transformations and start the import process to the target organization.
                        </div>
                        <div className="w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 mx-auto -mt-1" />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-Migration Confirmation modal */}
      <PreMigrationConfirmModal
        isOpen={showPreMigrationConfirm}
        onClose={() => setShowPreMigrationConfirm(false)}
        onConfirm={() => {
          setShowPreMigrationConfirm(false);
          void handleApproveAll();
        }}
        confirmLoading={startImport}
      />

      {/* Per-transformation details modal */}
      <Modal
        isOpen={modalState.isOpen}
        variant="transformationViewDetails"
        title={camelToWords(selectedTransformation?.name ?? '')}
        onClose={handleClose}
        cancelLabel="Close"
        showCloseButton={true}
      >
        {((): React.ReactNode => {
          if (!selectedTransformation) return null;
          const t = selectedTransformation;
          const _examples = (selectedJob?.job2Transform?.changesSummary?.examples ?? []).filter(
            (ex) => exampleMatchesTransformation(ex.transformationType, t.name)
          );
          const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : '—');
          const job = selectedJob?.job2Transform;
          return (
            <div className={`space-y-5 ${ui.text.label}`}>
              {/* Description */}
              {t.description && (
                <div
                  className={`flex items-start ${ui.gap.md} rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3`}
                >
                  <div
                    className={`${ui.widthAndHeight.iconContainer} rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0 mt-0.5`}
                  >
                    <svg
                      className="w-4 h-4 text-blue-500 dark:text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0h8v12H6V4zm1 2a1 1 0 000 2h4a1 1 0 100-2H7zm0 4a1 1 0 000 2h4a1 1 0 100-2H7z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <div
                      className={` ${ui.text.xxs} font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-0.5`}
                    >
                      Description
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 leading-snug">{t.description}</p>
                  </div>
                </div>
              )}

              {/* Job summary tiles */}
              {job && (
                <div className={`grid grid-cols-2 ${ui.gap.md}`}>
                  {(
                    [
                      {
                        label: 'Status',
                        value: job.status ?? '—',
                        icon: (
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        ),
                        iconBg: 'bg-blue-100 dark:bg-blue-900/40',
                        iconColor: 'text-blue-500 dark:text-blue-400'
                      },
                      {
                        label: 'Artifact Count',
                        value: job.artifactCount != null ? String(job.artifactCount) : '—',
                        icon: (
                          <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                        ),
                        iconBg: 'bg-purple-100 dark:bg-purple-900/40',
                        iconColor: 'text-purple-500 dark:text-purple-400'
                      },
                      {
                        label: 'Started',
                        value: fmt(job.startedAtUtc),
                        icon: (
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                            clipRule="evenodd"
                          />
                        ),
                        iconBg: 'bg-orange-100 dark:bg-orange-900/40',
                        iconColor: 'text-orange-500 dark:text-orange-400'
                      },
                      {
                        label: 'Completed',
                        value: fmt(job.completedAtUtc),
                        icon: (
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                            clipRule="evenodd"
                          />
                        ),
                        iconBg: 'bg-teal-100 dark:bg-teal-900/40',
                        iconColor: 'text-teal-500 dark:text-teal-400'
                      }
                    ] as {
                      label: string;
                      value: string;
                      icon: React.ReactNode;
                      iconBg: string;
                      iconColor: string;
                    }[]
                  ).map(({ label, value, icon, iconBg, iconColor }) => (
                    <div
                      key={label}
                      className={`flex items-center ${ui.gap.md} rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3 shadow-sm`}
                    >
                      <div
                        className={`${ui.widthAndHeight.iconContainerLg} rounded-lg ${iconBg} flex items-center justify-center shrink-0`}
                      >
                        <svg
                          className={`w-4 h-4 ${iconColor}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          {icon}
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <div
                          className={`${ui.text.xs} font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500`}
                        >
                          {label}
                        </div>
                        <div
                          className={`${ui.text.label} font-bold text-gray-800 dark:text-gray-100 truncate`}
                        >
                          {value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Changes summary stats */}
              {job?.changesSummary && (
                <div>
                  <div
                    className={`${ui.text.xs} font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3`}
                  >
                    Changes Summary
                  </div>
                  <div className={`grid grid-cols-3 ${ui.gap.md}`}>
                    {(
                      [
                        {
                          label: 'Names Changed',
                          value: job.changesSummary.namesChanged ?? 0,
                          icon: (
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          ),
                          iconBg: 'bg-blue-100 dark:bg-blue-900/40',
                          iconColor: 'text-blue-500 dark:text-blue-400',
                          tileBg:
                            'bg-blue-50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/40',
                          valColor: 'text-blue-700 dark:text-blue-300'
                        },
                        {
                          label: 'References Updated',
                          value: job.changesSummary.referencesUpdated ?? 0,
                          icon: (
                            <path
                              fillRule="evenodd"
                              d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
                              clipRule="evenodd"
                            />
                          ),
                          iconBg: 'bg-orange-100 dark:bg-orange-900/40',
                          iconColor: 'text-orange-500 dark:text-orange-400',
                          tileBg:
                            'bg-orange-50 dark:bg-orange-900/10 border-orange-100 dark:border-orange-900/40',
                          valColor: 'text-orange-700 dark:text-orange-300'
                        },
                        {
                          label: 'Division Assignments',
                          value: job.changesSummary.divisionAssignmentsApplied ?? 0,
                          icon: (
                            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
                          ),
                          iconBg: 'bg-purple-100 dark:bg-purple-900/40',
                          iconColor: 'text-purple-500 dark:text-purple-400',
                          tileBg:
                            'bg-purple-50 dark:bg-purple-900/10 border-purple-100 dark:border-purple-900/40',
                          valColor: 'text-purple-700 dark:text-purple-300'
                        },
                        {
                          label: 'Resource Types Modified',
                          value: job.changesSummary.filesModified ?? 0,
                          icon: (
                            <path
                              fillRule="evenodd"
                              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                              clipRule="evenodd"
                            />
                          ),
                          iconBg: 'bg-green-100 dark:bg-green-900/40',
                          iconColor: 'text-green-500 dark:text-green-400',
                          tileBg:
                            'bg-green-50 dark:bg-green-900/10 border-green-100 dark:border-green-900/40',
                          valColor: 'text-green-700 dark:text-green-300'
                        },
                        {
                          label: 'Resource Types Unchanged',
                          value: job.changesSummary.filesUnchanged ?? 0,
                          icon: (
                            <path
                              fillRule="evenodd"
                              d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0h8v12H6V4z"
                              clipRule="evenodd"
                            />
                          ),
                          iconBg: 'bg-gray-100 dark:bg-gray-700',
                          iconColor: 'text-gray-500 dark:text-gray-400',
                          tileBg:
                            'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
                          valColor: 'text-gray-700 dark:text-gray-300'
                        },
                        {
                          label: 'YAML Entries Removed',
                          value: job.changesSummary.yamlEntriesRemoved ?? 0,
                          icon: (
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          ),
                          iconBg: 'bg-yellow-100 dark:bg-yellow-900/40',
                          iconColor: 'text-yellow-600 dark:text-yellow-400',
                          tileBg:
                            'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-100 dark:border-yellow-900/40',
                          valColor: 'text-yellow-700 dark:text-yellow-300'
                        }
                      ] as {
                        label: string;
                        value: number;
                        icon: React.ReactNode;
                        iconBg: string;
                        iconColor: string;
                        tileBg: string;
                        valColor: string;
                      }[]
                    ).map(({ label, value, icon, iconBg, iconColor, tileBg, valColor }) => (
                      <div
                        key={label}
                        className={`flex items-center ${ui.gap.md} rounded-xl border px-3 py-3 ${tileBg}`}
                      >
                        <div
                          className={`${ui.widthAndHeight.iconContainerLg} rounded-lg ${iconBg} flex items-center justify-center shrink-0`}
                        >
                          <svg
                            className={`w-4 h-4 ${iconColor}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            {icon}
                          </svg>
                        </div>
                        <div>
                          <div
                            className={` ${ui.text.xxs} font-semibold text-gray-500 dark:text-gray-400 mt-0.5 leading-tight`}
                          >
                            {label}
                          </div>
                          <div
                            className={` ${ui.text.sectionTitle} font-extrabold leading-none ${valColor}`}
                          >
                            {value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {job?.notes && (
                <div
                  className={`flex items-start ${ui.gap.md} rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3`}
                >
                  <div
                    className={`${ui.widthAndHeight.iconContainer} rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center shrink-0 mt-0.5`}
                  >
                    <svg
                      className="w-4 h-4 text-blue-500 dark:text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <div
                      className={` ${ui.text.xxs} font-bold uppercase tracking-widest text-blue-500 dark:text-blue-400 mb-0.5`}
                    >
                      Notes
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-snug">
                      {job.notes}
                    </p>
                  </div>
                </div>
              )}

              {/* Errors */}
              {job?.errors && Array.isArray(job.errors) && job.errors.length > 0 && (
                <div
                  className={`flex items-start ${ui.gap.md} rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3`}
                >
                  <div
                    className={`${ui.widthAndHeight.iconContainer} rounded-lg bg-red-100 dark:bg-red-900/50 flex items-center justify-center shrink-0 mt-0.5`}
                  >
                    <svg
                      className="w-4 h-4 text-red-500 dark:text-red-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div
                      className={` ${ui.text.xxs} font-bold uppercase tracking-widest text-red-500 mb-0.5`}
                    >
                      Errors
                    </div>
                    <p
                      className={`${ui.text.xs} font-mono text-red-700 dark:text-red-300 whitespace-pre-wrap break-all`}
                    >
                      {job.errors.join('\n')}
                    </p>
                  </div>
                </div>
              )}

              {/* YAML Cleaned Files */}
              {(job?.changesSummary?.yamlCleanedFiles ?? []).length > 0 && (
                <div>
                  <div className={`flex items-center ${ui.gap.sm} ${ui.mb.sm}`}>
                    <div
                      className={`${ui.widthAndHeight.iconContainerSm} rounded-md bg-yellow-100 dark:bg-yellow-900/40 flex items-center justify-center shrink-0`}
                    >
                      <svg
                        className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0h8v12H6V4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span
                      className={`${ui.text.xs} font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500`}
                    >
                      YAML Cleaned Files
                    </span>
                    <span
                      className={`px-1.5 py-0.5 rounded-full ${ui.text.xs} font-bold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400`}
                    >
                      {job!.changesSummary!.yamlCleanedFiles!.length}
                    </span>
                  </div>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 max-h-44 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                    {job!.changesSummary!.yamlCleanedFiles!.map((f) => (
                      <div
                        key={f}
                        className={`flex items-center ${ui.gap.sm} px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0" />
                        <span
                          className={`${ui.text.xs} font-mono text-gray-600 dark:text-gray-300 truncate`}
                        >
                          {formatFilePath(f)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Examples for this transformation */}
              {_examples.length > 0 && (
                <div>
                  <div className={`flex items-center ${ui.gap.sm} ${ui.mb.sm}`}>
                    <div
                      className={`${ui.widthAndHeight.iconContainerSm} rounded-md bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0`}
                    >
                      <svg
                        className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path
                          fillRule="evenodd"
                          d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span
                      className={`${ui.text.xs} font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500`}
                    >
                      Examples
                    </span>
                    <span
                      className={`px-1.5 py-0.5 rounded-full ${ui.text.xs} font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400`}
                    >
                      {_examples.length}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {_examples.map((ex, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-4 py-3"
                      >
                        <div className={`flex flex-wrap ${ui.gap.xs} ${ui.mb.sm}`}>
                          {ex.transformationType && (
                            <span
                              className={`px-2 py-0.5 rounded-full ${ui.text.xs} font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300`}
                            >
                              {ex.transformationType}
                            </span>
                          )}
                          {ex.resourceType && (
                            <span
                              className={`px-2 py-0.5 rounded-full ${ui.text.xs} font-semibold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300`}
                            >
                              {ex.resourceType}
                            </span>
                          )}
                        </div>
                        <div className={`grid grid-cols-2 ${ui.gap.md}`}>
                          <div>
                            <div
                              className={`font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1 ${ui.text.xs}`}
                            >
                              Before
                            </div>
                            <pre
                              className={`font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all bg-white dark:bg-gray-900 rounded p-2 border border-gray-200 dark:border-gray-700 ${ui.text.sm}`}
                            >
                              {ex.originalValue}
                            </pre>
                          </div>
                          <div>
                            <div
                              className={`font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1 ${ui.text.xxs}`}
                            >
                              After
                            </div>
                            <pre
                              className={`font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all bg-white dark:bg-gray-900 rounded p-2 border border-gray-200 dark:border-gray-700 ${ui.text.sm}`}
                            >
                              {ex.transformedValue}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
    </>
  );
};

export default ApproveTransformationsPage;
